import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, DragStart, DropResult } from 'react-beautiful-dnd';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import useWindowDimensions, { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { ClidObjectType, Identifier, isSquadId, Media, MediaHistoryIpfxType, parseAgentKey, QueueOrSquadId, QueueRegionId, QueueSquadType, QueueView, sameQueueOrSquadId } from '../model';
import { actions } from '../store';
import { selectIsSingleQueueOrSquad, selectMyself, selectSingleQueueOrSquad } from '../store/reducers/config';
import { ColumnWidths, getGridSortOrder, selectExpandedQueues, selectFixedQueueGridHeights, selectSelectedQueueView, selectShowQueueAgents, selectShowQueuingMedia } from '../store/reducers/localSettings';
import { selectQueue } from '../store/reducers/queues';
import { useTeams } from '../TeamsProvider';
import AgentsTable from './AgentsTable';
import { UseCompactView } from './compactView';
import { FlexTable, Resizing, SortRules, TableColumn } from './FlexTable';
import { ReactComponent as ShowAgentsIcon } from './icons/show-agents.svg';
import { ReactComponent as ShowMediaIcon } from './icons/show-media.svg';
import MediaMenu, { MediaMenuState } from './MediaMenu';
import MediaTable from './MediaTable';
import './QueuesPage.scss';
import QueuesTable from './QueuesTable';
import { getQueueRegionName } from './RegionDropdownItems';
import { ItemSelected, RenderSelection } from './SearchPage';
import { SplitterDivider, StoredSplitter } from './Splitter';
import SquadsTable from './SquadsTable';

export default function QueuesPage() {
  console.log('Render QueuesPage')

  const dispatch = useDispatch()
  const view = useSelector(selectSelectedQueueView)
  const single = useSelector(selectSingleQueueOrSquad)
  const myId = useSelector(selectMyself)

  useEffect((() => {
    if (single) {
      if (isSquadId(single)) {
        dispatch(actions.fetchQueues.request({ region: QueueRegionId.Squads }))
      } else {
        dispatch(actions.fetchQueue.request(single))
      }
    } else if (view.region) {
      dispatch(actions.fetchQueues.request({ region: view.region }))
    }
  }), [dispatch, single, view, myId])

  if (!myId) return null

  return <>
    <QueuesPageHeader view={view} />
    {single && isSquadId(single) || view.region === QueueRegionId.Squads ? <SquadsTable /> : <QueuesTable />}
  </>
}

function QueuesPageHeader({ view }: { view: QueueView }) {
  const { inTeams } = useTeams()
  const isSingle = useSelector(selectIsSingleQueueOrSquad)
  const queue = useSelector(s => view.queue && selectQueue(s, view.queue))

  if (isSingle) { return null }

  return <h1 className={classNames('GridPageHeader', !inTeams && 'standalone', isMobileOnly && 'GridPageHeaderMobile')}>
    {view.region ? getQueueRegionName(view.region) : queue ? queue.description + ' [' + queue.id + ']' : ''}
  </h1>
}

interface QueuesPageTableProps<T extends QueueOrSquadId> extends HasClassName {
  data: T[]
  columns: TableColumn<T>[]
  loading: boolean
  getRowClass?(item: T): string | undefined
  expandable?(item: T): boolean
  onContextMenu?(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, item: T): void
}

export function QueuesPageTable<T extends QueueOrSquadId>({ className, data, columns, loading, getRowClass, expandable, onContextMenu }: QueuesPageTableProps<T>) {
  const dispatch = useDispatch()
  const { inTeams } = useTeams()
  const gridType = 'queues'
  const refColumnResizeState = useRef<ColumnWidths>()
  const sorted = useSelector(s => getGridSortOrder(s, gridType))

  const [mediaMenuState, setMediaMenuState] = useState<MediaMenuState>()

  const expanded = useSelector(selectExpandedQueues);
  const fixed = useSelector(selectFixedQueueGridHeights)
  const { height } = useWindowDimensions();

  useEffect(() => {
    return () => {
      if (mediaMenuState)
        delete mediaMenuState.onPreview
    }
  }, [mediaMenuState])


  const maxTableHeight = useMemo(() => {
    return Math.floor(height * fixed)
  }, [fixed, height])

  useEffect(() => {
    if (data.length === 1) {
      const { id, server, type } = data[0]
      const queueId: QueueOrSquadId = { id, server, type }
      if (!expanded.some(q => sameQueueOrSquadId(data[0], q))) {
        dispatch(actions.setExpandedQueues([queueId]))
      }
    }
  }, [data, dispatch, expanded])

  const expand = useCallback((item: T) => {
    dispatch(actions.expandQueue(item))
  }, [dispatch])

  const getRowProps = useCallback((item: T): React.HtmlHTMLAttributes<HTMLTableRowElement> => {
    return {
      onClick() {
        if (!expandable || expandable(item)) {
          dispatch(actions.expandQueue(item))
        }
      },
      onContextMenu: onContextMenu ? e => onContextMenu(e, item) : undefined,
      className: getRowClass ? getRowClass(item) : undefined,
    }
  }, [dispatch, expandable, getRowClass, onContextMenu])

  const onDragStart = useCallback((initial: DragStart) => {
    dispatch(actions.startDragMedia(initial.draggableId))
  }, [dispatch])

  const onDragEnd = useCallback((result: DropResult) => {
    transferMedia(dispatch, result)
    dispatch(actions.endDragMedia())
  }, [dispatch])

  const onSortBy = useCallback((sortBy: SortRules<T>) => {
    dispatch(actions.requestSetGridSortOrder(gridType, sortBy))
  }, [dispatch])

  const onColumnResize = useCallback((columnResize: Resizing) => {
    if (columnResize && columnResize.columnWidths) {
      for (const key in columnResize.columnWidths) {
        if (Object.prototype.hasOwnProperty.call(columnResize.columnWidths, key)) {
          if (refColumnResizeState.current) {
            refColumnResizeState.current = { ...refColumnResizeState.current, [key]: columnResize.columnWidths[key] }
          } else {
            refColumnResizeState.current = { [key]: columnResize.columnWidths[key] }
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  const children = useCallback((item: T) => {
    return <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className='subRows'>
        <Toggles id={item} />
        <AgentsTableMemo id={item.id} server={item.server} type={item.type} onMediaMenu={setMediaMenuState} maxTableHeight={maxTableHeight} />
        <MediaTableMemo id={item.id} server={item.server} type={item.type} onMediaMenu={setMediaMenuState} maxTableHeight={maxTableHeight} />
      </div>
    </DragDropContext>
  }, [maxTableHeight, onDragEnd, onDragStart])

  const table = useMemo(() => {

    function isExpanded(queue: QueueOrSquadId) {
      return expanded.some(q => sameQueueOrSquadId(queue, q))
    }

    return <FlexTable className={classNames('QueuesPageTable', !inTeams && 'standalone', className)}
      data={data}
      columns={columns}
      expandable={expandable || true}
      isExpanded={isExpanded}
      onExpand={expand}
      getRowProps={getRowProps}
      loading={loading}
      children={children}
      sortBy={sorted}
      onSortBy={onSortBy}
      onColumnResize={onColumnResize} />
  }, [children, className, columns, data, expand, expandable, expanded, getRowProps, inTeams, loading, onColumnResize, onSortBy, sorted],
  )

  return <UseCompactView>
    {table}
    {mediaMenuState && <MediaMenu state={mediaMenuState} onClose={() => {
      setMediaMenuState(undefined)
    }} />}
  </UseCompactView>
}

function MediaTableMemo({ id, server, type, onMediaMenu, maxTableHeight }: { id: string, server: string, type: QueueSquadType, onMediaMenu: (state?: MediaMenuState) => void, maxTableHeight?: number }) {
  const [selected, setSelected] = useState<ItemSelected | undefined>(undefined)
  const media = useMemo(() =>
    <StoredSplitter storeKey={'media-preview'} enable={selected !== undefined} initialValue={0.70} disabledWidth={'calc(100%)'}>
      <MediaTable id={id} server={server} type={type} onMediaMenu={(state) => {
        state.onPreview = (item) => {
          setSelected(convert(item))
        }
        onMediaMenu(state)
      }} maxTableHeight={maxTableHeight} />
      {selected && <SplitterDivider />}
      {selected && <RenderSelection className={''} selection={selected} onClose={() => {
        setSelected(undefined)
      }}
      />}
    </StoredSplitter>
    , [id, maxTableHeight, onMediaMenu, selected, server, type])

  return media
}

function AgentsTableMemo({ id, server, type, onMediaMenu, maxTableHeight }: { id: string, server: string, type: QueueSquadType, onMediaMenu: (state: MediaMenuState) => void, maxTableHeight?: number }) {
  const agents = useMemo(() => <AgentsTable id={id} server={server} type={type} onMediaMenu={onMediaMenu} maxTableHeight={maxTableHeight} />, [id, maxTableHeight, onMediaMenu, server, type])
  return agents
}

const transferMedia = (dispatch: Dispatch, { draggableId, destination }: DropResult) => {
  if (!destination) { return }

  const to = parseAgentKey(destination.droppableId)
  if (to.agent === null) { return }

  dispatch(actions.dropMedia(draggableId, to, destination.index))
}

function Toggles({ id }: { id: Identifier }) {
  const dispatch = useDispatch()
  const agents = useSelector(s => selectShowQueueAgents(s, id))
  const media = useSelector(s => selectShowQueuingMedia(s, id))

  return <div className='toggles'>
    <button title='Toggle agents' className={classNames(agents && 'active')}
      onClick={() => dispatch(actions.toggleQueueAgents(id))}>
      <ShowAgentsIcon />
    </button>

    <button title='Toggle media' className={classNames(media && 'active')}
      onClick={() => dispatch(actions.toggleQueuingMedia(id))}>
      <ShowMediaIcon />
    </button>
  </div>
}

function convert(item: Media) {
  const id = item.mediaType === 'CALL' || item.mediaType === 'VM' ? item.id : item.mediaItemId
  const ipfxType = item.clidObjectType === ClidObjectType.extension ? MediaHistoryIpfxType.Extension : item.clidObjectType === ClidObjectType.companyContact || item.clidObjectType === ClidObjectType.customerContact ? MediaHistoryIpfxType.Contact : MediaHistoryIpfxType.None

  return { type: item.mediaType, id: id, isOutgoing: item.mediaType === 'OUT', notes: item.notes, ipfxId: item.clidObjectKey, ipfxType: ipfxType, accountId: '' } as ItemSelected
}
